<script>
import { format, parseISO } from 'date-fns/fp'
import { isToday, isFuture } from 'date-fns'
import { authComputed, authMethods } from '@state/helpers'
import store from '@state/store'
import AddToCalendar from '@events/components/add-to-calendar.vue'
import RegistrationForm from '@events/components/registration-form.vue'

export default {
  name: 'EventDetails',
  components: { AddToCalendar, RegistrationForm },
  props: {
    oneEvent: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      newMessage: '',
      joinedModalVisible: false,
      joinedTheWaitlist: false,
      isJoining: false,
      isPosting: false,
      registrationModalVisible: false,
      currentRegData: null,
      currentRegDataUserId: null,
      currentGuestsRegData: null,
      recurringJoinModal: false,
      recurringQuitModal: false,
    }
  },
  computed: {
    ...authComputed,
    event() {
      const eventDetails =
        this.$store.state.events.eventDetails[this.oneEvent.id] || {}
      return {
        ...this.oneEvent,
        ...eventDetails,
        ...eventDetails.other_details,
      }
    },
    companyId() {
      return this.$route.params.companyId
    },
    isInEvent() {
      return (
        this.event.joinedUsers &&
        !!this.event.joinedUsers.find(({ id }) => id === this.currentUserId)
      )
    },
    joinedUsersCount() {
      if (!this.event.joinedUsers) return this.event.joined_users_count

      const getGuestsCount = (acc, { pivot }) => acc + (pivot.guests_count || 0)
      const guestsCount = this.event.joinedUsers.reduce(getGuestsCount, 0)

      return this.event.joinedUsers.length + guestsCount
    },

    // I can join if the event is in the future and there are still places available
    isEligibleToJoin() {
      return this.isInFuture && !this.isFull
    },
    canJoinWaitList() {
      // event is in the future and is already full and I am not joined (neither normally or in join list)
      return (
        this.isInFuture &&
        this.isFull &&
        !this.isInEvent &&
        !this.hasUserJoinedWaitlist
      )
    },
    isFull() {
      const { max_people: maxPeople } = this.event
      return maxPeople <= this.joinedUsersCount
    },
    isInFuture() {
      const { start } = this.event
      return isToday(parseISO(start)) || isFuture(parseISO(start))
    },
    hasUserJoinedWaitlist() {
      const event = this.event
      const joinedWaitlistUsers = event.joinedWaitlistUsers
      return (
        joinedWaitlistUsers &&
        !!joinedWaitlistUsers.find(({ id }) => id === this.currentUserId)
      )
    },
    hasRegistrationForm() {
      return !!(
        this.event.has_registration_form && this.event.registration_form_fields
      )
    },
    maxGuestsCount() {
      return this.event.max_people - this.joinedUsersCount - 1
    },
    hasGuestsEnabled() {
      let hasGuestsEnabled = false

      if (this.event.has_guests_enabled) {
        hasGuestsEnabled = true
      }

      if (
        (this.event.max_people <= this.joinedUsersCount &&
          !!this.event.waitlist) ||
        !this.maxGuestsCount
      ) {
        hasGuestsEnabled = false
      }

      return !!hasGuestsEnabled
    },
    joinedPercentage() {
      return parseInt((this.joinedUsersCount / this.event.max_people) * 100)
    },
    month() {
      return format('MMM', parseISO(this.event.start))
    },
    dayNumber() {
      return format('d', parseISO(this.event.start))
    },
    dayName() {
      return format('EEEE', parseISO(this.event.start))
    },
    hoursFormatted() {
      return (
        format('hh:mm a', parseISO(this.event.start)) +
        ' - ' +
        format('hh:mm a', parseISO(this.event.end))
      )
    },
    eventCauses() {
      return (
        this.event.causes &&
        this.event.causes.map(({ name }) => name).join(', ')
      )
    },
    eventTags() {
      return (
        this.event.tags && this.event.tags.map(({ name }) => name).join(', ')
      )
    },
    eventSchoolGrade() {
      return (
        this.event.schoolGrade &&
        this.event.schoolGrade.map(({ name }) => name).join(', ')
      )
    },
    eventInteractingWith() {
      return (
        this.event.interactingWith &&
        this.event.interactingWith.map(({ name }) => name).join(', ')
      )
    },
    mapUrl() {
      const address = encodeURIComponent(
        this.event.address_1 +
          ' ' +
          this.event.address_2 +
          ' ' +
          this.event.city +
          ', ' +
          this.event.state +
          ' ' +
          this.event.zip +
          ', USA'
      )
      return (
        'https://maps.google.com/maps?q=' + address + '&z=15&output=svembed'
      )
    },
    isLeader() {
      return (
        this.event.leaders &&
        !!this.event.leaders.find(({ id }) => id === this.currentUserId)
      )
    },
    isOwner() {
      return parseInt(this.event.user_id) === this.currentUserId
    },
    isEligibleToPostMessage() {
      const date = parseISO(this.event.start)
      const isInFuture = isToday(date) || isFuture(date)

      return !!isInFuture
    },
    isRecurring() {
      return !!this.event.recurring_enabled
    },
  },
  async mounted() {
    // fetch event details
    await store.dispatch('events/fetchEventDetails', {
      eventId: this.oneEvent.id,
    })
  },
  methods: {
    ...authMethods,
    // return true if the current user is the guest of the this event
    isGuest() {
      // TODO
    },
    canEditOpportunity(userId = null) {
      var can = false
      if (
        this.isOwner ||
        this.isLeader ||
        this.canEditCompany(this.companyId) ||
        (userId && userId === this.currentUserId)
      ) {
        can = true
      }
      return can
    },
    joinModal() {
      if (this.isRecurring) {
        this.recurringJoinModal = true
      } else {
        this.joinEvent()
      }
    },
    joinModalClose() {
      this.recurringJoinModal = false
    },
    // join events without registration form or guests enabled
    joinEvent(recurring) {
      this.joinModalClose()
      if (!this.isEligibleToJoin) return

      this.isJoining = true
      const params = {
        eventId: this.event.id,
        userId: this.currentUserId,
      }
      if (recurring === true) {
        Object.assign(params, { recurring: true })
      }

      if (!this.hasRegistrationForm && !this.hasGuestsEnabled) {
        return store
          .dispatch('events/joinEvent', params)
          .then((response) => this.afterJoin(response))
          .catch(() => alert('Something went wrong.'))
          .finally(() => (this.isJoining = false))
      }
    },
    afterJoin(response) {
      this.joinedModalVisible = true
      for (var i = 0; i < response.length; i++) {
        if (response[i].id === this.currentUser.id) {
          if (response[i].status_update) {
            store.commit(
              'companies/ADD_STATUS_UPDATE',
              response[i].status_update
            )
          }
          break
        }
      }
    },
    joinEventWaitlist() {
      // TODO make sure we can join
      this.isJoining = true
      if (!this.hasRegistrationForm && !this.hasGuestsEnabled) {
        return store
          .dispatch('events/joinEventWaitlist', {
            eventId: this.event.id,
            userId: this.currentUserId,
          })
          .then(() => {
            this.joinedModalVisible = true
            this.joinedTheWaitlist = true
          })
          .catch(() => alert('Something went wrong.'))
          .finally(() => (this.isJoining = false))
      }
    },

    quitModal() {
      if (this.isRecurring) {
        this.recurringQuitModal = true
      } else {
        this.quitEvent()
      }
    },
    quitModalClose() {
      this.recurringQuitModal = false
    },
    quitEvent(recurring) {
      this.quitModalClose()
      this.isJoining = true
      const params = {
        eventId: this.event.id,
        userId: this.currentUserId,
      }
      if (recurring === true) {
        Object.assign(params, { recurring: true })
      }

      return store
        .dispatch('events/quitEvent', params)
        .then(() =>
          alert('You have been successfully unsubscribed to this event!')
        )
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isJoining = false))
    },
    quitEventWaitlist() {
      this.isJoining = true
      return store
        .dispatch('events/quitEventWaitlist', {
          eventId: this.event.id,
          userId: this.currentUserId,
        })
        .then(() =>
          alert(
            'You have been successfully unsubscribed to this event waitlist!'
          )
        )
        .catch(() => alert('Something went wrong.'))
        .finally(() => (this.isJoining = false))
    },
    removeUser(userId) {
      if (!this.canEditOpportunity()) return

      return store
        .dispatch('events/removeJoinedUser', { eventId: this.event.id, userId })
        .then(({ removedUser: { name } }) =>
          alert(`You have succesfully removed "${name}" from the attendees!`)
        )
        .catch(() => alert('Something went wrong.'))
    },
    postMessage() {
      if (!this.isEligibleToPostMessage) return

      this.isPosting = true

      store
        .dispatch('events/setEventMessages', {
          eventId: this.event.id,
          message: this.newMessage,
          userId: this.currentUserId,
        })
        .then(() => {
          this.newMessage = ''
          this.isPosting = false
        })
        .catch(() => alert('Something went wrong.'))
        .finally(
          () =>
            (this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight)
        )
    },
    onEditEventClick(name) {
      this.$router.push({
        name,
        params: { companyId: this.companyId, eventId: this.oneEvent.id },
      })
    },
    pinEvent() {
      store
        .dispatch('events/pinEvent', { eventId: this.event.id })
        .then(() =>
          alert('Action succeeded. Please refresh the page to update the list!')
        )
        .catch(() => alert('Something went wrong.'))
    },
    unpinEvent() {
      store
        .dispatch('events/unpinEvent', { eventId: this.event.id })
        .then(() =>
          alert('Action succeeded. Please refresh the page to update the list!')
        )
        .catch(() => alert('Something went wrong.'))
    },
    openRegModal(userId, regData, guestsRegData) {
      this.currentRegData = regData
      this.currentRegDataUserId = userId
      this.currentGuestsRegData = guestsRegData
      this.registrationModalVisible = true
    },
    onJoined(response) {
      this.joinedTheWaitlist = response.waitlist
      this.registrationModalVisible = false
      this.joinedModalVisible = true
      this.isJoining = false
      this.afterJoin(response.users)
    },
    shareEvent() {
      store
        .dispatch('companies/postNewStatusUpdate', {
          type: 'event',
          userId: this.currentUser.id,
          updatableId: this.companyId,
          updatableType: 'company',
          postAnonymous: false,
          opportunityId: this.oneEvent.id,
        })
        .then((newPost) => {
          alert('This event has been shared to your feed.')
          this.$emit('close')
        })
        .catch(() => {})
    },
  },
}
</script>

<template>
  <BaseModalWindow
    modalcls="view-event bg-white rounded-xl mx-auto shadow-box relative left-0 flex justify-between max-w-event-modal overflow-hidden"
    :visible="true"
    @close="$emit('close')"
  >
    <template v-slot:body>
      <div class="w-4/12 mr-7.5 mb-5">
        <img :src="event.image_url" :alt="event.name" />
        <div class="pb-10 pl-8">
          <div class="flex justify-between -mt-8">
            <div class="flex">
              <div
                class="mr-4 text-center align-middle bg-white rounded-full shadow-datestamp w-22 h-22"
              >
                <div class="mx-auto my-3 text-basel text-blue-darker">
                  {{ month
                  }}<div class="leading-tight text-32xl">{{ dayNumber }}</div>
                </div>
              </div>
              <div class="pt-10 text-lg align-middle text-blue-light">
                Hosted by {{ event.group_name }}
              </div>
            </div>
            <div class="block mt-10 mr-2">
              <v-popover
                v-if="canEditOpportunity()"
                trigger="click"
                placement="bottom"
                :auto-hide="true"
              >
                <button class="cursor-pointer">
                  <BaseIcon name="settings" :size="22" class="text-blue" />
                </button>

                <template slot="popover">
                  <div class="text-lg text-blue-darker">
                    <a
                      class="block cursor-pointer"
                      @click="onEditEventClick('editEventDetails')"
                    >
                      Details
                    </a>
                    <a
                      class="block cursor-pointer"
                      @click="onEditEventClick('editEventSettings')"
                    >
                      Settings
                    </a>
                    <a
                      class="block cursor-pointer"
                      @click="onEditEventClick('editEventCheckin')"
                    >
                      Check In
                    </a>

                    <a
                      v-if="!event.pinned"
                      class="block cursor-pointer"
                      @click="pinEvent"
                    >
                      Pin
                    </a>
                    <a v-else class="block cursor-pointer" @click="unpinEvent">
                      Unpin
                    </a>
                  </div>
                </template>
              </v-popover>
            </div>
          </div>
          <div class="mt-3 text-2xl text-gray-light">
            {{ dayName }} {{ hoursFormatted }}
          </div>
          <button
            class="flex items-center h-12 mt-4 space-x-2 leading-loose button primary-button"
            @click="shareEvent"
          >
            <BaseIcon name="share" />
            <span>Share Opportunity</span>
          </button>
          <div v-if="event.min_age !== ''" class="flex justify-between">
            <div class="mt-3">
              <div class="text-base text-blue-darker">
                Minimum Volunteer Age:
              </div>
              <div class="text-2xl text-gray-light">{{ event.min_age }}</div>
            </div>
          </div>
          <div class="mt-3">
            <div class="text-base text-blue-darker">
              Location:
            </div>
            <div class="mt-1 text-lg text-blue">
              {{ event.location_name }}<br v-if="event.location_name !== ''" />
              {{ event.address_1 }}<br v-if="event.address_1 !== ''" />
              {{ event.address_2 }}<br v-if="event.address_2 !== ''" />
              {{ event.city }}, {{ event.state }} {{ event.zip }}
            </div>
          </div>
          <div class="w-full mt-3">
            <iframe :src="mapUrl" frameborder="0" width="100%"></iframe>
          </div>
          <div v-if="event.joinedUsers" class="mt-3">
            <div class="text-base text-blue-darker">
              Who Is Going:
            </div>
            <div class="flex mt-3">
              <div
                class="progress border border-gray-dark rounded-2xl h-7.5 w-300 mr-3 overflow-hidden"
              >
                <div
                  class="progress-bar rounded-2xl bg-volunteer_red h-7.5 -ml-1px -mt-1px transition-all duration-200 ease-out"
                  role="progressbar"
                  :aria-valuenow="joinedPercentage"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :style="{ width: joinedPercentage + '%' }"
                ></div>
              </div>
              <div class="text-lg text-volunteer_red">
                {{ joinedUsersCount }}/{{ event.max_people }}
              </div>
            </div>
            <div class="h-56 mt-3 overflow-x-hidden overflow-y-scroll">
              <div
                v-for="user in event.joinedUsers"
                :key="user.id"
                class="flex items-center justify-between mb-3"
              >
                <div class="ml-4">
                  <img
                    :src="user.image_url"
                    class="inline-block w-8 h-8 mr-3 rounded-full"
                    alt=""
                  />
                  <span
                    :class="{
                      'font-semibold': isInEvent && currentUserId === user.id,
                    }"
                    class="space-x-2 text-blue-darker text-sml"
                  >
                    <span v-text="user.name" />
                    <span
                      v-if="user.pivot.guests_count"
                      v-text="`(+${user.pivot.guests_count})`"
                    />
                  </span>
                </div>
                <div class="flex items-center mr-3 space-x-1">
                  <template v-if="hasRegistrationForm">
                    <button
                      v-for="{ pivot, guests } in [user]"
                      :key="guests.length"
                      class="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-darker text-sml hover:text-blue-darker hover:bg-gray-lighter"
                      @click="
                        openRegModal(
                          user.id,
                          pivot.registration_form_data,
                          // eslint-disable-next-line vue/comma-dangle
                          guests
                        )
                      "
                    >
                      <BaseIcon name="settings" :size="12" />
                    </button>
                  </template>
                  <button
                    v-if="canEditOpportunity()"
                    class="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-darker text-sml hover:text-blue-darker hover:bg-gray-lighter"
                    @click="removeUser(user.id)"
                  >
                    <BaseIcon name="close" />
                  </button>
                </div>
              </div>
            </div>

            <div v-if="event.waitlist" class="text-base text-blue-darker">
              Waitlist (
              {{
                event.joinedWaitlistUsers && event.joinedWaitlistUsers.length
              }}
              ):
            </div>
            <div
              v-if="event.waitlist"
              class="h-56 mt-3 overflow-x-hidden overflow-y-scroll"
            >
              <div
                v-for="user in event.joinedWaitlistUsers"
                :key="user.id"
                class="flex items-center justify-between mb-3"
              >
                <div class="ml-4">
                  <img
                    :src="user.image_url"
                    class="inline-block w-8 h-8 mr-3 rounded-full"
                    alt=""
                  />
                  <span
                    :class="{
                      'font-semibold': currentUserId === user.id,
                    }"
                    class="text-blue-darker text-sml"
                  >
                    {{ user.name }}
                  </span>
                </div>
                <button
                  v-if="canEditOpportunity(user.id)"
                  class="inline-flex items-center justify-center w-5 h-5 mr-3 rounded-full text-gray-darker text-sml hover:text-blue-darker hover:bg-gray-lighter"
                  @click="removeUser(user.id)"
                >
                  <BaseIcon name="close" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-8/12 pt-5 mb-5 pr-11">
        <h3 class="text-4xl text-blue-darker">
          {{ event.name }}
        </h3>

        <div v-if="!isGuest()">
          <!-- join -->
          <!-- if I can join  (enough place and not already joined)-->
          <span v-if="!isInEvent && isEligibleToJoin">
            <!-- without registration form and without guests -->
            <button
              v-if="!hasRegistrationForm && !hasGuestsEnabled"
              class="h-12 mt-4 mb-2 leading-loose button primary-button"
              :class="{ 'text-gray bg-gray': isJoining }"
              :disabled="isJoining"
              @click="joinModal"
              v-text="isJoining ? 'Joining...' : 'Join Volunteer Opportunity'"
            />
            <!-- event with guests or registration form or both -->
            <button
              v-if="hasRegistrationForm || hasGuestsEnabled"
              class="h-12 mt-4 mb-2 leading-loose button primary-button"
              :class="{ 'text-gray bg-gray': isJoining }"
              :disabled="isJoining"
              @click="
                registrationModalVisible = true
                isJoining = true
              "
              v-text="isJoining ? 'Joining...' : 'Join Volunteer Opportunity'"
            />
          </span>
          <!-- join waitlist -->
          <span v-if="canJoinWaitList">
            <!-- without registration form and without guests -->
            <button
              v-if="!hasRegistrationForm && !hasGuestsEnabled"
              class="h-12 mt-4 mb-2 leading-loose button primary-button"
              :class="{ 'text-gray bg-gray': isJoining }"
              :disabled="isJoining"
              @click="joinEventWaitlist"
              v-text="
                isJoining
                  ? 'Joining...'
                  : `Join Waitlist (${
                      !event.joinedWaitlistUsers
                        ? 0
                        : event.joinedWaitlistUsers.length
                    })`
              "
            />
            <!-- event with guests or registration form or both -->
            <button
              v-else
              class="h-12 mt-4 mb-2 leading-loose button primary-button"
              :class="{ 'text-gray bg-gray': isJoining }"
              :disabled="isJoining"
              @click="
                registrationModalVisible = true
                isJoining = true
              "
              v-text="
                isJoining
                  ? 'Joining...'
                  : `Join Waitlist (${
                      !event.joinedWaitlistUsers
                        ? 0
                        : event.joinedWaitlistUsers.length
                    })`
              "
            />
          </span>
          <RegistrationForm
            v-if="registrationModalVisible"
            :current-user-id="currentUserId"
            :event="event"
            :form-fields="event.registration_form_fields"
            :current-reg-data="currentRegData"
            :current-guests-reg-data="currentGuestsRegData"
            @joined="onJoined"
            @close="
              registrationModalVisible = false
              isJoining = false
              currentRegData = null
              currentRegDataUserId = null
              currentGuestsRegData = null
            "
          />
          <!-- quit event-->
          <button
            v-if="isInEvent"
            class="h-12 mt-4 mb-2 leading-loose button primary-button"
            :disabled="isJoining"
            @click="quitModal"
            v-text="isJoining ? 'Quitting...' : 'Quit Volunteer Opportunity'"
          />

          <button
            v-if="hasUserJoinedWaitlist && !isInEvent"
            class="h-12 mt-4 mb-2 leading-loose button primary-button"
            :disabled="isJoining"
            @click="quitEventWaitlist"
            v-text="isJoining ? 'Leaving...' : 'Leave Waitlist'"
          />
        </div>
        <!-- not a guest -->
        <div v-if="isGuest()"> </div>

        <AddToCalendar
          v-if="joinedModalVisible"
          :event="event"
          :joined-the-waitlist="joinedTheWaitlist"
          @close="joinedModalVisible = false"
        />

        <BaseModalWindow
          v-if="recurringJoinModal"
          modalcls="bg-white rounded-xl mx-auto px-11 shadow-box relative left-0 flex justify-between overflow-hidden max-w-1.95xl"
          :visible="recurringJoinModal"
          @close="joinModalClose"
        >
          <template v-slot:body>
            <div class="w-full mx-auto mt-12 mb-5">
              <div class="">
                Do you want to join this event only or all the following events,
                also?
              </div>

              <div class="mt-5 text-center">
                <button
                  class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mr-4"
                  @click="joinEvent"
                >
                  Join Single
                </button>
                <button
                  class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
                  @click="joinEvent(true)"
                >
                  Join All
                </button>
              </div>
            </div>
          </template>
        </BaseModalWindow>
        <BaseModalWindow
          v-if="recurringQuitModal"
          modalcls="bg-white rounded-xl mx-auto px-11 shadow-box relative left-0 flex justify-between overflow-hidden max-w-1.95xl"
          :visible="recurringQuitModal"
          @close="quitModalClose"
        >
          <template v-slot:body>
            <div class="w-full mx-auto mt-12 mb-5">
              <div class="">
                Do you want to quit this event or all the following events,
                also?
              </div>

              <div class="mt-5 text-center">
                <button
                  class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mr-4"
                  @click="quitEvent"
                >
                  Quit Single
                </button>
                <button
                  class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3"
                  @click="quitEvent(true)"
                >
                  Quit All
                </button>
              </div>
            </div>
          </template>
        </BaseModalWindow>

        <div v-if="event.leaders.length" class="mt-3">
          <div class="text-base text-blue-darker">
            Volunteer Opportunity leader{{
              event.leaders.length > 1 ? 's' : ''
            }}:
          </div>
          <div class="">
            <ul class="space-y-2">
              <li
                v-for="leader in event.leaders"
                :key="leader.id"
                class="flex items-center space-x-3"
              >
                <img
                  :src="leader.image_url"
                  class="w-5 h-5 rounded-full"
                  :alt="leader.name"
                />
                <span>{{ leader.name }}</span>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="event.agenda !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            Agenda:
          </div>
          <div class="">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.agenda }}</pre
            >
          </div>
        </div>
        <div v-if="event.what_to_do !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            What We Will Do:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.what_to_do }}</pre
            >
          </div>
        </div>
        <div v-if="event.what_to_bring !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            What To Bring:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.what_to_bring }}</pre
            >
          </div>
        </div>
        <div v-if="event.where_to_park !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            Where To Park:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.where_to_park }}</pre
            >
          </div>
        </div>
        <div v-if="event.where_to_meet !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            Where To Meet:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.where_to_meet }}</pre
            >
          </div>
        </div>
        <div v-if="event.details !== ''" class="mt-3">
          <div class="text-base text-blue-darker">
            Other Details:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.details }}</pre
            >
          </div>
        </div>
        <div
          v-if="event.point_of_contact && event.point_of_contact !== ''"
          class="mt-3"
        >
          <div class="text-base text-blue-darker">
            Point Of Contact:
          </div>
          <div class="mt-3">
            <pre
              class="my-3 text-black whitespace-pre-wrap font-proxima text-basel"
              >{{ event.point_of_contact }}</pre
            >
          </div>
        </div>
        <div class="mt-3">
          <div v-if="eventCauses" class="text-base text-blue-darker">
            Causes: {{ eventCauses }}
          </div>
          <div v-if="eventTags" class="text-base text-blue-darker">
            Tags: {{ eventTags }}
          </div>
          <div v-if="eventSchoolGrade" class="text-base text-blue-darker">
            School Level: {{ eventSchoolGrade }}
          </div>
          <div v-if="eventInteractingWith" class="text-base text-blue-darker">
            Interacting with: {{ eventInteractingWith }}
          </div>
        </div>
        <div class="mt-3">
          <div class="text-base text-blue-darker">
            Message Board:
          </div>
          <div
            ref="messages"
            class="w-full h-56 p-4 mt-1 overflow-x-hidden overflow-y-scroll border-2 outline-none appearance-none resize-y border-gray rounded-input"
          >
            <div
              v-for="message in event.messages"
              :key="message.id"
              class="flex justify-between w-300"
            >
              <div>
                <span
                  :class="[
                    isInEvent && currentUserId === message.user.id
                      ? 'font-semibold'
                      : '',
                  ]"
                  class="text-blue-darker text-sml"
                  >{{ message.user.name }}:</span
                >
                {{ message.message }}
              </div>
            </div>
          </div>
          <div v-if="isEligibleToPostMessage">
            <input
              v-model="newMessage"
              class="w-full h-12 px-4 mt-2 border-2 outline-none appearance-none border-blue-lighter rounded-input"
              placeholder="Comment..."
              type="text"
              @keyup.enter="postMessage"
            />
            <button
              :class="{ 'text-gray bg-gray': isPosting }"
              class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
              @click="postMessage"
              v-text="isPosting ? 'Posting...' : 'Post'"
            />
          </div>
        </div>
      </div>
    </template>
  </BaseModalWindow>
</template>
